import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { ModuleIcon } from '@/constants/content'

import {
    CalendarDays,
    CircleDashed,
    FolderOpen,
    Gamepad2,
    Hammer,
    Route,
    Sparkles
} from 'lucide-react'

import { SectionHeading } from '@/components/generals/section-heading'
import { StatusLabel } from '@/components/generals/status-label'

import { vision } from '@/constants/content'

const moduleIcons: Record<ModuleIcon, LucideIcon> = {
    gamepad: Gamepad2,
    calendar: CalendarDays,
    sparkles: Sparkles,
    folder: FolderOpen,
    route: Route
}

const VisionSection: FC = (): ReactNode => {
    return (
        <section id='vision' aria-labelledby='vision-heading' className='relative border-t border-soft-white/10 py-24 sm:py-32'>
            <div className='container-site'>
                <SectionHeading
                    id='vision-heading'
                    eyebrow={vision.eyebrow}
                    title={vision.heading}
                    intro={vision.intro}
                    aside={<StatusLabel status='futureVision' />}
                />

                <div className='reveal relative mt-14'>
                    <div
                        aria-hidden
                        className='absolute top-0 bottom-0 left-[1.75rem] w-px bg-linear-to-b from-orange/60 via-soft-white/15 to-transparent lg:top-[1.75rem] lg:right-[10%] lg:bottom-auto lg:left-[10%] lg:h-px lg:w-auto lg:bg-linear-to-r lg:from-orange/50 lg:via-soft-white/20 lg:to-orange/50'
                    />

                    <ol className='relative grid gap-5 lg:grid-cols-5 lg:gap-4'>
                        {vision.modules.map(platformModule => {
                            const Icon = moduleIcons[platformModule.icon]

                            return (
                                <li key={platformModule.name} className='flex items-start gap-4 lg:flex-col lg:items-center lg:text-center'>
                                    <span className='relative grid size-14 shrink-0 place-items-center rounded-2xl bg-navy-800 text-orange shadow-[0_10px_30px_-12px_rgb(0_0_0/0.8)] ring-1 ring-soft-white/15'>
                                        <Icon className='size-6' aria-hidden />
                                    </span>

                                    <div className='rounded-xl border border-soft-white/10 bg-navy-900/60 p-4 lg:w-full'>
                                        <p className='font-heading text-base font-bold text-soft-white'>
                                            {platformModule.name}
                                        </p>

                                        <p className='mt-1 text-sm text-cool-grey-light'>
                                            {platformModule.detail}
                                        </p>
                                    </div>
                                </li>
                            )
                        })}
                    </ol>

                    <p className='mt-6 text-sm text-cool-grey-light lg:text-center'>
                        {vision.modulesNote}
                    </p>
                </div>

                <div className='mt-16 grid gap-5 lg:grid-cols-[1.15fr_1fr_1fr]'>
                    <div className='reveal relative overflow-hidden rounded-xl bg-[linear-gradient(160deg,#1B3350,#0D1C2D)] p-7 ring-1 ring-orange/25 sm:p-8'>
                        <div aria-hidden className='absolute -bottom-20 -left-16 size-56 rounded-full bg-orange/10 blur-3xl' />

                        <StatusLabel status='inDevelopment' />

                        <h3 className='relative mt-5 text-2xl font-bold tracking-tight text-soft-white sm:text-3xl'>
                            {vision.statement.title}
                        </h3>

                        <p className='relative mt-4 leading-relaxed text-cool-grey-light'>
                            {vision.statement.body}
                        </p>
                    </div>

                    <div className='reveal rounded-xl border border-orange/25 bg-navy-900/60 p-7'>
                        <p className='flex items-center gap-2 text-xs font-semibold tracking-[0.16em] text-orange'>
                            <Hammer className='size-4' aria-hidden />
                            IN DEVELOPMENT
                        </p>

                        <ul className='mt-5 flex flex-col gap-3'>
                            {vision.inDevelopment.map(item => (
                                <li key={item} className='flex gap-3 text-soft-white/90'>
                                    <span aria-hidden className='mt-2 size-1.5 shrink-0 rounded-full bg-orange' />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className='reveal rounded-xl border border-dashed border-soft-white/20 bg-navy-900/30 p-7'>
                        <p className='flex items-center gap-2 text-xs font-semibold tracking-[0.16em] text-cool-grey-light'>
                            <CircleDashed className='size-4' aria-hidden />
                            FUTURE VISION
                        </p>

                        <ul className='mt-5 flex flex-col gap-3'>
                            {vision.futureVision.map(item => (
                                <li key={item} className='flex gap-3 text-cool-grey-light'>
                                    <span aria-hidden className='mt-2 size-1.5 shrink-0 rounded-full border border-cool-grey-light' />
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    )
}

export default VisionSection
